import { createAction, createReducer } from '@reduxjs/toolkit';
import {
  IAction,
  IParceirosRepasse,
  IPeriodoUltimos12Meses,
} from './interfaces/apiInterfaces';
import {
  IInitialState,
  ILastTwelveMonths,
  IPartnerPassedOn,
} from './interfaces/reduxInterfaces';

const INITIAL_STATE: IInitialState = {
  isActive: false,
  isLoading: false,
  gotSuccess: false,
  gotError: false,
  dateSearch: {
    start: '',
    end: '',
  },
  accounts: {
    closedAccounts: 0,
    openedAccounts: 0,
    partner: 'Fidúcia',
    type: 'Global',
    total: 0,
    activeAccounts: 0,
    pendingAccounts: 0,
    rejectedAccounts: 0,
    blockedAccounts: 0,
  },
  faresNotReceived: {
    quantity: 0,
    value: 0,
  },
  faresPassedOn: {
    quantity: 0,
    value: 0,
  },
  faresReceived: {
    quantity: 0,
    value: 0,
  },
  faresToReceive: {
    quantity: 0,
    value: 0,
  },
  income: {
    value: 0,
  },
  result: 0,
  provider: {
    quantity: 0,
    value: 0,
  },
  partnerPassedOn: [],
  lastTwelveMonths: [],
};

export const getMonthlyFaresFromApi = createAction(
  '@faresApp/monthlyFares/GET_MONTHLY_FARES_FROM_API'
);

export const gotMonthlyFaresFromApi = createAction<any>(
  '@faresApp/monthlyFares/GOT_MONTHLY_FARES_FROM_API'
);

export const gotErrorFromMonthlyFaresApi = createAction(
  '@faresApp/monthlyFares/GOT_ERROR_FROM_MONTHLY_FARES_API'
);

export const displayMonthlyFares = createAction<boolean>(
  '@faresApp/monthlyFares/DISPLAY_MONTHLY_FARES'
);

export const logoutMonthlyFaresApp = createAction(
  '@clientsApp/LOGOUT_MONTHLY_FARES_APP'
);

function correctionForApiFailure(object: any) {
  if (object.repassado === undefined) {
    const mes = {
      data: object.data,
      recebida: {
        valor: object.recebida.valor,
        quantidade: object.recebida.quantidade,
      },
      repassado: {
        quantidade: object.tarifas_repassadas.quantidade,
        valor: object.tarifas_repassadas.valor,
      },
    };
    return mes;
  }
  return object;
}

const monthlyFaresReducer = createReducer(INITIAL_STATE, {
  [getMonthlyFaresFromApi.type]: (state) => ({
    ...state,
    isActive: false,
    isLoading: true,
    gotSuccess: false,
    gotError: false,
  }),
  [gotMonthlyFaresFromApi.type]: (state, action: IAction) => ({
    ...state,
    isActive: true,
    gotSuccess: true,
    gotError: false,
    isLoading: false,
    dateSearch: {
      start: action.payload.data_pesquisa.de,
      end: action.payload.data_pesquisa.ate,
    },
    accounts: {
      closedAccounts: action.payload.conta.quantidade_contas_encerradas,
      openedAccounts: action.payload.conta.quantidade_contas_abertas,
      partner: action.payload.conta.parceiro,
      type: action.payload.conta.tipo,
      total: action.payload.conta.totalizador_contas,
      activeAccounts: action.payload.resumo_conta.ativas,
      pendingAccounts: action.payload.resumo_conta.pendentes,
      rejectedAccounts: action.payload.resumo_conta.rejeitadas,
      blockedAccounts: action.payload.resumo_conta.bloqueadas,
    },
    faresNotReceived: {
      quantity: action.payload.nao_recebida.quantidade_tarifa_nao_recebida,
      value: action.payload.nao_recebida.valor_nao_recebida,
    },
    faresPassedOn: {
      quantity: action.payload.repassado.quantidade_tarifa_repassado,
      value: action.payload.repassado.valor_repassado,
    },
    faresReceived: {
      quantity: action.payload.recebida.quantidade_tarifa_recebida,
      value: action.payload.recebida.valor_recebido,
    },
    faresToReceive: {
      quantity: action.payload.receber_futuro.quantidade_a_receber,
      value: action.payload.receber_futuro.valor_a_receber,
    },
    income: {
      value: action.payload.receita.valor,
    },
    result: action.payload.resultado,
    provider: {
      quantity: action.payload.provisionado.quantidade,
      value: action.payload.provisionado.valor,
    },
    partnerPassedOn: action.payload.parceiros_repasse.map(
      (parceiro: IParceirosRepasse) => {
        const data: IPartnerPassedOn = {
          partner: parceiro.parceiro,
          amountPassedOn: parceiro.quantidade_repasse,
          valuePassedOn: parceiro.valor_repasse,
          valueReceived: parceiro.valor_recebido,
          income: parceiro.receita,
        };
        return data;
      }
    ),
    lastTwelveMonths: action.payload.periodo_ultimos_12_meses.map(
      (mes: IPeriodoUltimos12Meses) => {
        const novoMes = correctionForApiFailure(mes);
        const data: ILastTwelveMonths = {
          date: novoMes.data,
          faresReceived: {
            value: novoMes.recebida.valor,
            quantity: novoMes.recebida.quantidade,
          },
          faresPassedOn: {
            value: novoMes.repassado.valor,
            quantity: novoMes.repassado.quantidade,
          },
        };
        return data;
      }
    ),
  }),
  [gotErrorFromMonthlyFaresApi.type]: (state: IInitialState) => ({
    ...state,
    isActive: false,
    isLoading: false,
    gotSuccess: false,
    gotError: true,
  }),
  [displayMonthlyFares.type]: (state: IInitialState, action) => ({
    ...state,
    isActive: action.payload,
  }),
  [logoutMonthlyFaresApp.type]: (state: IInitialState) => ({
    ...state,
    isActive: false,
    isLoading: false,
    gotSuccess: false,
    gotError: false,
    dateSearch: {
      start: '',
      end: '',
    },
    accounts: {
      closedAccounts: 0,
      openedAccounts: 0,
      partner: 'Fidúcia',
      type: 'Global',
      total: 0,
      activeAccounts: 0,
      pendingAccounts: 0,
      rejectedAccounts: 0,
      blockedAccounts: 0,
    },
    faresNotReceived: {
      quantity: 0,
      value: 0,
    },
    faresPassedOn: {
      quantity: 0,
      value: 0,
    },
    faresReceived: {
      quantity: 0,
      value: 0,
    },
    faresToReceive: {
      quantity: 0,
      value: 0,
    },
    income: {
      value: 0,
    },
    provider: {
      quantity: 0,
      value: 0,
    },
    partnerPassedOn: [],
    lastTwelveMonths: [],
  }),
});

export default monthlyFaresReducer;
